/**
 * The PIN keypad.
 *
 * Rendered by the app, not by the platform keyboard, for the reason in
 * agents/security.md 2.2: a password field invites password managers and extensions to
 * treat a PIN as a credential worth saving, and a warehouse tablet is exactly where you do
 * not want that. Nothing here autofills, autocompletes, or persists.
 */
import { useCallback, useEffect, useState } from 'react';

export interface PinPadProps {
  onSubmit: (pin: string) => void;
  disabled?: boolean;
  label?: string;
  /** Cleared from the outside after a failure, so the operator starts fresh. */
  resetToken?: number;
}

const PIN_LENGTH = 6;
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'] as const;

export function PinPad({ onSubmit, disabled = false, label = 'Enter your PIN', resetToken }: PinPadProps) {
  const [digits, setDigits] = useState('');

  useEffect(() => {
    setDigits('');
  }, [resetToken]);

  const push = useCallback(
    (key: string) => {
      if (disabled) return;
      if (key === 'clear') {
        setDigits('');
        return;
      }
      if (key === 'back') {
        setDigits((current) => current.slice(0, -1));
        return;
      }
      setDigits((current) => {
        const next = current.length >= PIN_LENGTH ? current : current + key;
        if (next.length === PIN_LENGTH) {
          // Submit on the sixth digit. One less tap on a tablet with gloves on.
          queueMicrotask(() => onSubmit(next));
        }
        return next;
      });
    },
    [disabled, onSubmit],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key >= '0' && event.key <= '9') push(event.key);
      else if (event.key === 'Backspace') push('back');
      else if (event.key === 'Escape') push('clear');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [push]);

  return (
    <div className="a-pinpad">
      <p className="a-pinpad__label" id="pinpad-label">
        {label}
      </p>
      <div className="a-pinpad__dots" role="status" aria-live="polite" aria-label={`${digits.length} of 6 digits`}>
        {Array.from({ length: PIN_LENGTH }, (_, index) => (
          <span
            key={index}
            className={index < digits.length ? 'a-pinpad__dot a-pinpad__dot--on' : 'a-pinpad__dot'}
          />
        ))}
      </div>
      <div className="a-pinpad__grid" role="group" aria-labelledby="pinpad-label">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className={key === 'clear' || key === 'back' ? 'a-pinpad__key a-pinpad__key--quiet' : 'a-pinpad__key'}
            onClick={() => push(key)}
            disabled={disabled}
            aria-label={key === 'back' ? 'Delete last digit' : key === 'clear' ? 'Clear' : key}
          >
            {key === 'back' ? '⌫' : key === 'clear' ? 'Clear' : key}
          </button>
        ))}
      </div>
    </div>
  );
}
